/**
 * Rides Management permissions.
 *
 * Module capability — Admin / RidesAdmin get everything; RidesSecurity gets the security
 * desk (expected drivers, check marks, physical-document verification); any other user
 * gets a capability only when they hold the matching `Rides.*` right (rights come from the
 * identity service and can be granted per user without a role).
 *
 * The capability rules live in `computeRidesPermissions` (pure, no hooks) so the route gates
 * (RidesGate via useRidesPermissions) and the sidebar menu (ridesMenu.tsx via
 * ridesMenuVisibility) evaluate exactly the same thing. Which capability opens which page is
 * RIDES_PAGE_ACCESS; both the routes and the menu read it, so they cannot drift apart.
 *
 * The UI only hides what the user cannot do; the API enforces the same rules.
 */

import { useMemo } from 'react';
import { RIDES_RIGHTS } from '@actschurch/ccms-sdk';
import type { MenuItemVisibility } from '../../layouts/menu/types';
import { useEnrichedAuth } from '../useEnrichedAuth';

export { RIDES_RIGHTS };

export const RIDES_ALL_ROLES = ['Admin', 'RidesAdmin', 'RidesSecurity'];
export const RIDES_ADMIN_ROLES = ['Admin', 'RidesAdmin'];

interface RidesAccessChecks {
  hasRole: (role: string) => boolean;
  hasPermission: (right: string) => boolean;
}

/** Every Carpool capability for a user, from plain role / right checks (no React). */
export function computeRidesPermissions({ hasRole, hasPermission }: RidesAccessChecks) {
  const isRidesAdmin = hasRole('Admin') || hasRole('RidesAdmin');
  const isSecurity = hasRole('RidesSecurity');

  /** Capability = admin, or a role that grants it by default, or the explicit right. */
  const can = (right: string, roleDefault: boolean) => isRidesAdmin || roleDefault || hasPermission(right);

  const canRead = can(RIDES_RIGHTS.Read, isSecurity);
  const canViewSecurity = can(RIDES_RIGHTS.SecurityCheck, isSecurity);
  const canViewVerification = can(RIDES_RIGHTS.VerificationRead, isSecurity);
  const canRecordVerification = can(RIDES_RIGHTS.VerificationRecord, isSecurity);
  const canManageDrivers = can(RIDES_RIGHTS.DriversManage, false);
  const canManageBookings = can(RIDES_RIGHTS.BookingsManage, false);
  const canManageDisputes = can(RIDES_RIGHTS.DisputesManage, false);
  const canManageCampuses = can(RIDES_RIGHTS.CampusesManage, false);
  const canManageEvents = can(RIDES_RIGHTS.EventsManage, false);
  const canManageSettings = can(RIDES_RIGHTS.SettingsManage, false);
  /** May open the admin console (dashboard, drivers, bookings…) — not just the security desk. */
  const canViewAdmin =
    isRidesAdmin ||
    canManageDrivers ||
    canManageBookings ||
    canManageDisputes ||
    canManageCampuses ||
    canManageEvents ||
    canManageSettings ||
    (!isSecurity && canRead);

  return {
    canRead,
    canViewAdmin,
    canViewSecurity,
    canViewVerification,
    canRecordVerification,
    canManageDrivers,
    canManageBookings,
    canManageDisputes,
    canManageCampuses,
    canManageEvents,
    canManageSettings,
    isRidesAdmin,
    isSecurity,
    /** Security-only user: no admin capability at all — land them on the security desk. */
    isSecurityOnly: isSecurity && !canViewAdmin,
  };
}

export type RidesCapabilities = ReturnType<typeof computeRidesPermissions>;

export function useRidesPermissions() {
  const { user, hasRole, hasPermission } = useEnrichedAuth();

  const perms = useMemo(() => computeRidesPermissions({ hasRole, hasPermission }), [hasRole, hasPermission]);

  return {
    ...perms,
    userId: user?.id as string | undefined,
  };
}

export type RidesPermissions = ReturnType<typeof useRidesPermissions>;

/** Who may open a Carpool page: one predicate per page, shared by the routes and the menu. */
export type RidesPageAccess = (p: RidesCapabilities) => boolean;

export const RIDES_PAGE_ACCESS = {
  /** Security-only users are redirected to the security desk instead. */
  dashboard: (p) => p.canViewAdmin,
  drivers: (p) => p.canViewAdmin,
  driverDetail: (p) => p.canViewAdmin || p.canViewVerification,
  verifications: (p) => p.canViewVerification,
  documents: (p) => p.canViewVerification,
  bookings: (p) => p.canViewAdmin,
  listings: (p) => p.canViewAdmin,
  disputes: (p) => p.canViewAdmin,
  security: (p) => p.canViewSecurity,
  campuses: (p) => p.canViewAdmin,
  events: (p) => p.canViewAdmin,
  settings: (p) => p.canManageSettings,
  copy: (p) => p.canManageSettings,
  lookups: (p) => p.canManageSettings,
} satisfies Record<string, RidesPageAccess>;

/**
 * Menu visibility that matches the route gate: the menu context carries the same roles and
 * rights (user.roles / user.rights) that useEnrichedAuth checks, so a page shows in the menu
 * exactly when RidesGate would let the person in. Pass several pages for a section.
 */
export function ridesMenuVisibility(...pages: RidesPageAccess[]): MenuItemVisibility {
  return (ctx) => {
    const roles = ctx.userRoles ?? [];
    const rights = ctx.userPermissions ?? [];
    const perms = computeRidesPermissions({
      hasRole: (role) => roles.includes(role),
      hasPermission: (right) => rights.includes(right),
    });
    return pages.some((allow) => allow(perms));
  };
}
